import { createContext, memo, type PropsWithChildren, useContext, useMemo } from 'react';

import {
  type WorkspaceMemberResponseWithRoles,
  WorkspaceMemberRoleDisplayName,
  WorkspaceMemberType,
  type WorkspaceUserRole,
} from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useAccountContext } from '~/providers/AccountProvider';
import { useWorkspaceRolesContext } from '~/providers/WorkspaceRolesProvider';
import { useFetchMembers } from '~/swr-hooks/members/useFetchMembers';
import { isDevOrTestStage } from '~/swr-hooks/utils';

type CurrentWorkspaceMemberProviderProps = PropsWithChildren<object>;

export type CurrentWorkspaceMemberProviderContextValue = {
  currentWorkspaceMember?: WorkspaceMemberResponseWithRoles & {
    role?: WorkspaceUserRole;
    isSysAdmin?: boolean;
    isWorkspaceOwner?: boolean;
  };
};

const defaultValue: CurrentWorkspaceMemberProviderContextValue = {
  currentWorkspaceMember: undefined,
};

const CurrentWorkspaceMemberProviderContext = createContext<CurrentWorkspaceMemberProviderContextValue>(defaultValue);

export const CurrentWorkspaceMemberProvider = memo(({ children }: CurrentWorkspaceMemberProviderProps) => {
  const { data: members } = useFetchMembers();
  const { data: roles } = useWorkspaceRolesContext();
  const { data: account } = useAccountContext();

  const data = useMemo(() => {
    const currentMember = members?.find((member) => member.accountId === account?.id);

    const role = roles?.find(({ id }) => id === currentMember?.roleId);

    return {
      currentWorkspaceMember: currentMember
        ? {
            ...currentMember,
            role,
            isSysAdmin: role?.type === WorkspaceMemberType.sysadmin,
            isWorkspaceOwner: !role?.isCustom && role?.displayName === WorkspaceMemberRoleDisplayName.WorkspaceOwner,
          }
        : undefined,
    };
  }, [account?.id, members, roles]);

  return (
    <CurrentWorkspaceMemberProviderContext.Provider value={data}>
      {children}
    </CurrentWorkspaceMemberProviderContext.Provider>
  );
});

CurrentWorkspaceMemberProvider.displayName = 'CurrentWorkspaceMemberProvider';

export const useCurrentWorkspaceMemberContext = () => {
  const context = useContext(CurrentWorkspaceMemberProviderContext);

  if (context === defaultValue) {
    const error = 'CurrentWorkspaceMemberProviderContext used outside of CurrentWorkspaceMemberProvider';

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
